import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { functions, db, auth } from '../utils/firebase';
import * as Sentry from '@sentry/browser';

/**
 * Creates a standard error response object
 */
const createErrorResponse = (error) => ({
  merged: false,
  survivingBeerId: '',
  mergingBeerId: '',
  favoritesReassigned: 0,
  reviewsReassigned: 0,
  status: '',
  error,
});

/**
 * Validates merge data and returns an error message if validation fails
 */
const validateMergeData = (mergeData) => {
  if (!mergeData || typeof mergeData !== 'object') {
    return 'Invalid merge data';
  }

  const { breweryId, survivingBeerId, mergingBeerId, mergedBeer } = mergeData;

  if (!breweryId || typeof breweryId !== 'string' || !breweryId.trim()) {
    return 'Brewery ID is required';
  }

  if (!survivingBeerId || typeof survivingBeerId !== 'string' || !survivingBeerId.trim()) {
    return 'Surviving beer ID is required';
  }

  if (!mergingBeerId || typeof mergingBeerId !== 'string' || !mergingBeerId.trim()) {
    return 'Merging beer ID is required';
  }

  if (survivingBeerId === mergingBeerId) {
    return 'Surviving beer ID and merging beer ID must be different';
  }

  if (!mergedBeer || typeof mergedBeer !== 'object') {
    return 'Merged beer data is required';
  }

  const { name, style, abv } = mergedBeer;

  if (!name || typeof name !== 'string' || !name.trim()) {
    return 'Beer name is required';
  }

  if (!style || typeof style !== 'string' || !style.trim()) {
    return 'Beer style is required';
  }

  if (!Number.isFinite(abv) || abv <= 0) {
    return 'ABV must be a positive number';
  }

  if (!Number.isInteger(abv * 10)) {
    return 'ABV must have maximum 1 decimal place';
  }

  return null;
};

/**
 * Saves merge record to manualBeerMerges collection
 */
const saveMergeRecord = async (beer1, beer2) => {
  if (!beer1 || !beer2) {
    return;
  }

  try {
    const currentUser = auth.currentUser;
    const username = currentUser?.displayName || currentUser?.email || 'Unknown';

    const mergeRecord = {
      beer1: {
        name: beer1.name || '',
        style: beer1.style || '',
        abv: beer1.abv || 0,
        description: beer1.description || null,
      },
      beer2: {
        name: beer2.name || '',
        style: beer2.style || '',
        abv: beer2.abv || 0,
        description: beer2.description || null,
      },
      dateTime: serverTimestamp(),
      username,
    };

    await addDoc(collection(db, 'manualBeerMerges'), mergeRecord);
  } catch (recordError) {
    console.error('Failed to save merge record:', recordError);
    Sentry.captureException(recordError);
  }
};

/**
 * useBeerMerge
 * Provides a callable-based merge operation for beers.
 *
 * @returns {{ mergeBeers: Function, loading: boolean, errorMessage: string, clearError: Function }}
 */
export function useBeerMerge() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  /**
   * Merge two beers into one.
   *
   * @param {Object} mergeData - Beer merge data
   * @param {string} mergeData.breweryId - Must exist in breweries collection
   * @param {string} mergeData.survivingBeerId - First beer ID - this beer will survive the merge
   * @param {string} mergeData.mergingBeerId - Second beer ID - this beer will be deleted after merge
   * @param {Object} mergeData.mergedBeer - New data for the merged beer
   * @param {string} mergeData.mergedBeer.name - Beer name for the merged beer (will be trimmed)
   * @param {string} mergeData.mergedBeer.style - Must exist as a document ID in beerStyles collection (will be trimmed)
   * @param {number} mergeData.mergedBeer.abv - Must be a positive number with maximum 1 decimal place
   * @param {string|null} mergeData.mergedBeer.description - Optional beer description
   * @param {Object} mergeData.beer1 - Original beer1 data (name, style, abv, description)
   * @param {Object} mergeData.beer2 - Original beer2 data (name, style, abv, description)
   * @returns {Promise<{ merged: boolean, survivingBeerId: string, mergingBeerId: string, favoritesReassigned: number, reviewsReassigned: number, status: string, error?: string }>}
   */
  const mergeBeers = useCallback(async (mergeData) => {
    const validationError = validateMergeData(mergeData);
    if (validationError) {
      return createErrorResponse(validationError);
    }

    const { breweryId, survivingBeerId, mergingBeerId, mergedBeer, beer1, beer2 } = mergeData;
    const { name, style, abv, description } = mergedBeer;

    if (!isMountedRef.current) {
      return createErrorResponse('');
    }

    setLoading(true);
    setErrorMessage('');

    try {
      await saveMergeRecord(beer1, beer2);

      const mergeBeersFn = httpsCallable(
        functions,
        'callableBeerMergeBeers-mergeBeers',
      );
      const result = await mergeBeersFn({
        breweryId: breweryId.trim(),
        survivingBeerId: survivingBeerId.trim(),
        mergingBeerId: mergingBeerId.trim(),
        mergedBeer: {
          name: name.trim(),
          style: style.trim(),
          abv,
          description: description?.trim() || null,
        },
      });

      const response = result?.data || {};

      return {
        merged: response.merged === true,
        survivingBeerId: response.survivingBeerId || survivingBeerId,
        mergingBeerId: response.mergingBeerId || mergingBeerId,
        favoritesReassigned: response.favoritesReassigned || 0,
        reviewsReassigned: response.reviewsReassigned || 0,
        status: response.status || '',
      };
    } catch (err) {
      Sentry.captureException(err);
      if (isMountedRef.current) {
        const friendly =
          err?.message || 'Failed to merge beers. Please try again or contact support.';
        setErrorMessage(friendly);
        return {
          ...createErrorResponse(friendly),
          survivingBeerId: survivingBeerId || '',
          mergingBeerId: mergingBeerId || '',
        };
      }
      return createErrorResponse('');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  return {
    mergeBeers,
    loading,
    errorMessage,
    clearError,
  };
}
